import { zodResolver } from '@hookform/resolvers/zod';
import { HTTPError } from 'ky';
import { Check, KeyRound, SquarePen } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import userPlaceholder from '@/assets/placeholders/user.png';
import { toast } from '@/lib/notify';

import { useChangePassword } from '../api/useChangePassword';
import { type UserProfile, useGetMe } from '../api/useGetMe';
import { useUpdateProfile } from '../api/useUpdateProfile';
import {
  type ChangePasswordInput,
  changePasswordSchema,
  type ProfileInput,
  profileSchema,
} from '../schemas/settingsSchemas';
import { roleDisplayLabel } from '../utils/roleLabel';
import { PasswordInput } from './PasswordInput';

const SECURITY_REQUIREMENTS = [
  'Min. 8 characters',
  'Uppercase letter',
  'Numeric digit',
  'Special character',
] as const;

export function ProfileCard() {
  const meQuery = useGetMe();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();

  const profile = meQuery.data;
  const email = profile?.email ?? '';
  const roleLabel = profile
    ? roleDisplayLabel({ roleName: profile.roleName, isSuperAdmin: profile.isSuperAdmin })
    : 'User';
  const displayName = profile?.fullName ?? 'Your name';

  const profileForm = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: '', lastName: '', phone: '' },
  });

  useEffect(() => {
    if (!profile) return;
    profileForm.reset({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      phone: profile.phoneNumber ?? '',
    });
  }, [profile, profileForm]);

  const passwordForm = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSaveProfile = profileForm.handleSubmit(async (values) => {
    try {
      await updateProfile.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phone ? values.phone : null,
      });
      toast.success('Profile updated');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Could not update profile.'));
    }
  });

  const onChangePassword = passwordForm.handleSubmit(async (values) => {
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success('Password changed');
      passwordForm.reset();
    } catch (error) {
      const code = await extractProblemCode(error);
      if (code === 'Auth.CurrentPasswordIncorrect') {
        passwordForm.setError('currentPassword', { message: 'Current password is incorrect.' });
        return;
      }
      toast.error(extractErrorMessage(error, 'Could not change password.'));
    }
  });

  return (
    <div className="w-full grid lg:grid-cols-2 gap-5 items-start">
      {/* ── Left column: Personal Information ─────────────────────────── */}
      <div className="border border-surface-soft-border rounded-[20px] p-4 lg:p-5 text-black-logo space-y-4">
        <h5 className="text-base lg:text-lg lg:leading-[22px] font-semibold">
          Personal Information
        </h5>
        <div className="w-full h-px bg-grayEF" />

        {/* Avatar + identity */}
        <div className="flex flex-col items-center gap-1 text-center">
          <div className="relative w-fit shrink-0">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-placeholder/15">
              <img src={userPlaceholder} alt="" className="w-full h-full object-cover" />
            </div>
            <label
              htmlFor="upload-profile"
              aria-label="Change profile photo"
              className="absolute end-0.5 bottom-0.5 w-4.5 h-4.5 flex items-center justify-center rounded-md bg-primary text-white shadow-[0_4px_4px_0_rgba(0,0,0,0.1)] cursor-pointer overflow-hidden"
            >
              <input
                id="upload-profile"
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
              />
              <SquarePen className="w-3 h-3 pointer-events-none" />
            </label>
          </div>
          <h5 className="text-base leading-[19px] font-semibold mt-1.5">{displayName}</h5>
          <p className="text-sm leading-[17px] text-gray66">{email || ' '}</p>
          <div className="rounded-full py-[3px] px-2.5 text-primary bg-blue-tint">{roleLabel}</div>
        </div>

        {/* Personal info form */}
        <form noValidate onSubmit={onSaveProfile} className="grid sm:grid-cols-2 gap-4">
          <Field
            id="profile-first-name"
            label="First Name"
            error={profileForm.formState.errors.firstName?.message}
          >
            <input
              id="profile-first-name"
              type="text"
              className="sc-input py-2.5"
              placeholder="First Name"
              autoComplete="given-name"
              {...profileForm.register('firstName')}
            />
          </Field>
          <Field
            id="profile-last-name"
            label="Last Name"
            error={profileForm.formState.errors.lastName?.message}
          >
            <input
              id="profile-last-name"
              type="text"
              className="sc-input py-2.5"
              placeholder="Last Name"
              autoComplete="family-name"
              {...profileForm.register('lastName')}
            />
          </Field>
          <Field id="profile-email" label="Email Address">
            <input
              id="profile-email"
              type="email"
              className="sc-input py-2.5"
              value={email}
              readOnly
              autoComplete="email"
            />
          </Field>
          <Field
            id="profile-phone"
            label="Phone Number"
            error={profileForm.formState.errors.phone?.message}
          >
            <input
              id="profile-phone"
              type="tel"
              className="sc-input py-2.5"
              placeholder="Phone Number"
              autoComplete="tel"
              {...profileForm.register('phone')}
            />
          </Field>
          <div className="sm:col-span-2 text-center">
            <button
              type="submit"
              className="btn btn-primary px-2.5! mx-auto"
              disabled={updateProfile.isPending || !meQuery.data}
            >
              {updateProfile.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Right column: Security & Password ──────────────────────────── */}
      <div className="border border-surface-soft-border rounded-[20px] p-4 lg:p-5 text-black-logo space-y-4">
        <h5 className="text-base lg:text-lg lg:leading-[22px] font-semibold">
          Security &amp; Password
        </h5>
        <div className="w-full h-px bg-grayEF" />

        <form noValidate onSubmit={onChangePassword} className="grid grid-cols-1 gap-4">
          <Field
            id="current-password"
            label="Current Password"
            error={passwordForm.formState.errors.currentPassword?.message}
          >
            <PasswordInput
              id="current-password"
              placeholder="Current Password"
              autoComplete="current-password"
              {...passwordForm.register('currentPassword')}
            />
          </Field>
          <Field
            id="new-password"
            label="New Password"
            error={passwordForm.formState.errors.newPassword?.message}
          >
            <PasswordInput
              id="new-password"
              placeholder="New Password"
              autoComplete="new-password"
              {...passwordForm.register('newPassword')}
            />
          </Field>
          <Field
            id="confirm-password"
            label="Confirm New Password"
            error={passwordForm.formState.errors.confirmPassword?.message}
          >
            <PasswordInput
              id="confirm-password"
              placeholder="Confirm New Password"
              autoComplete="new-password"
              {...passwordForm.register('confirmPassword')}
            />
          </Field>
          <div className="text-center lg:text-end">
            <button
              type="submit"
              className="btn btn-outlined px-2.5! text-primary! border-primary! hover:bg-primary/10!"
              disabled={changePassword.isPending}
            >
              {changePassword.isPending ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </form>

        {/* Security requirements checklist */}
        <div className="p-4 border border-blue-tint bg-surface-soft rounded-xl space-y-2.5 lg:space-y-[13px]">
          <div className="flex items-center gap-1.5">
            <div className="shrink-0 rounded-full bg-blue-tint w-6 h-6 flex items-center justify-center">
              <KeyRound className="w-4 h-4" />
            </div>
            <h5 className="text-base leading-[150%] font-semibold">Security Requirements</h5>
          </div>
          <ul className="grid min-[440px]:grid-cols-2 gap-2">
            {SECURITY_REQUIREMENTS.map((requirement) => (
              <li key={requirement} className="flex items-center gap-1.5 text-sm leading-[150%]">
                <Check className="w-3.5 h-3.5 shrink-0" />
                <span>{requirement}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ id, label, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="sc-input-label text-black">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

async function extractProblemCode(error: unknown): Promise<string | null> {
  if (!(error instanceof HTTPError)) {
    return null;
  }
  try {
    const body = (await error.response.clone().json()) as {
      errors?: Array<{ code?: string }>;
    };
    return body.errors?.[0]?.code ?? null;
  } catch {
    return null;
  }
}

export type { UserProfile };
